import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { logError } from 'services/logging/logging';
import { useImportDocuments } from 'services/repositories/integrations/integrations';
import { processSubmissionErrors } from 'support/helpers/errors/errors';
import { ImportDocumentsResponseDTO } from 'support/types';
import { BasePopup } from 'components/Display/BasePopup/BasePopup';
import { Button } from 'components/Form/Button';
import { Form } from 'components/Form/Form';
import { Textarea } from 'components/Form/Input/Textarea/Textarea';
import { ValidationErrors } from 'components/Form/ValidationErrors/ValidationErrors';

import { useImportableIntegrations } from '../../hooks/useImportableIntegrations';
import { MAX_IMPORT_DOCUMENTS, parseDocumentNumbers } from './helpers';
import { ImportDocumentsTargetPicker } from './ImportDocumentsTargetPicker';
import { ImportResults } from './ImportResults';

const FORM_ID = 'import-documents-form';

type ImportDocumentsModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Pre-resolved target. When either is omitted, the modal shows a source-system / document-type picker. */
  integrationId?: string;
  processId?: string;
  /** Optional pre-fill for the document-numbers field (e.g. the document number searched in the list). */
  prefill?: Array<string>;
};

type FormValues = {
  documentNumbers: string;
};

export const ImportDocumentsModal = ({
  open,
  setOpen,
  integrationId,
  processId,
  prefill,
}: ImportDocumentsModalProps) => {
  const { t } = useTranslation();
  const [result, setResult] = useState<ImportDocumentsResponseDTO | null>(null);
  const [topLevelErrors, setTopLevelErrors] = useState<Array<string>>([]);
  const { mutate: importDocuments, isLoading } = useImportDocuments();

  const importableIntegrations = useImportableIntegrations();
  const showPicker = !integrationId || !processId;
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<string | undefined>(integrationId);
  const [selectedProcessId, setSelectedProcessId] = useState<string | undefined>(processId);

  const targetIntegrationId = integrationId ?? selectedIntegrationId;
  const targetProcessId = processId ?? selectedProcessId;
  const hasTarget = Boolean(targetIntegrationId && targetProcessId);

  // The source system the documents are pulled from, named for the description copy. Falls back to
  // the first importable integration before one is picked — they all share a supported connector.
  const sourceConnector =
    importableIntegrations.find((integration) => integration.id === targetIntegrationId)?.connector ??
    importableIntegrations[0]?.connector;

  const schema = Yup.object().shape({
    documentNumbers: Yup.string()
      .default('')
      .test(
        'required',
        t('transactions:list.importDocuments.validation.required'),
        (value) => parseDocumentNumbers(value ?? '').length >= 1,
      )
      .test(
        'max',
        t('transactions:list.importDocuments.validation.max', { max: MAX_IMPORT_DOCUMENTS }),
        (value) => parseDocumentNumbers(value ?? '').length <= MAX_IMPORT_DOCUMENTS,
      ),
  });

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { documentNumbers: prefill?.join('\n') ?? '' },
    resolver: yupResolver(schema),
  });

  const handleClose = () => {
    setOpen(false);
  };

  const startNewImport = () => {
    setResult(null);
    setTopLevelErrors([]);
    reset({ documentNumbers: '' });
  };

  const onSubmit = ({ documentNumbers }: FormValues) => {
    if (!targetIntegrationId || !targetProcessId) {
      return;
    }

    setTopLevelErrors([]);
    const parsed = parseDocumentNumbers(documentNumbers);

    importDocuments(
      { integrationId: targetIntegrationId, processId: targetProcessId, documentNumbers: parsed },
      {
        onSuccess: (response) => {
          setResult(response);
        },
        onError: (error) => {
          logError(error);
          processSubmissionErrors({
            error,
            setInputError: setError,
            setNonInputErrors: setTopLevelErrors,
            defaultData: { documentNumbers: '' },
            t,
          });
        },
      },
    );
  };

  return (
    <BasePopup open={open} setOpen={setOpen} width="md">
      <BasePopup.Header title={t('transactions:list.importDocuments.modal.title')} />
      <BasePopup.Body className="flex flex-col gap-6">
        {result ? (
          <ImportResults result={result} />
        ) : (
          <>
            <ValidationErrors errors={topLevelErrors} />

            <p className="text-sm text-gray-500">
              {t('transactions:list.importDocuments.modal.description', {
                connector: t(`integrations:connector.${sourceConnector}.name`),
              })}
            </p>

            {showPicker && (
              <ImportDocumentsTargetPicker
                integrations={importableIntegrations}
                selectedIntegrationId={selectedIntegrationId}
                onSelectIntegration={setSelectedIntegrationId}
                selectedProcessId={selectedProcessId}
                onSelectProcess={setSelectedProcessId}
              />
            )}

            <Form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
              <Textarea
                {...register('documentNumbers')}
                label={t('transactions:list.importDocuments.field.label')}
                description={t('transactions:list.importDocuments.field.help', { max: MAX_IMPORT_DOCUMENTS })}
                required
                rows={6}
                errors={errors.documentNumbers}
              />
            </Form>
          </>
        )}

        <div className="flex items-center justify-end gap-2">
          {result ? (
            <>
              <Button analyticsId="transactions:import_documents_close" variant="secondary" onClick={handleClose}>
                {t('transactions:list.importDocuments.close')}
              </Button>
              <Button analyticsId="transactions:import_documents_more" variant="primary" onClick={startNewImport}>
                {t('transactions:list.importDocuments.importMore')}
              </Button>
            </>
          ) : (
            <>
              <Button
                analyticsId="transactions:import_documents_cancel"
                variant="secondary"
                onClick={handleClose}
                disabled={isLoading}
              >
                {t('transactions:list.importDocuments.cancel')}
              </Button>
              <Button
                analyticsId="transactions:import_documents_submit"
                variant="primary"
                type="submit"
                form={FORM_ID}
                loading={isLoading}
                disabled={!hasTarget}
              >
                {t('transactions:list.importDocuments.submit')}
              </Button>
            </>
          )}
        </div>
      </BasePopup.Body>
    </BasePopup>
  );
};
